import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { localStorageService, Theme } from "../services/localStorage";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Check localStorage first, then system preference, fallback to light
    const saved = localStorageService.getTheme();
    if (saved && (saved === "light" || saved === "dark")) {
      return saved;
    }

    if (typeof window !== "undefined") {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      return prefersDark ? "dark" : "light";
    }
    
    return "light";
  });

  useEffect(() => {
    // Apply theme to document root
    document.documentElement.classList.toggle("dark", theme === "dark");
    
    // Update iOS status bar style with actual theme colors
    const statusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    
    if (statusBarMeta && themeColorMeta) {
      if (theme === "dark") {
        // Dark theme: background is hsl(240 10% 3.9%) which is #0a0a0b
        statusBarMeta.setAttribute("content", "black-translucent");
        themeColorMeta.setAttribute("content", "#0a0a0b");
      } else {
        // Light theme: background is hsl(0 0% 100%) which is #ffffff
        statusBarMeta.setAttribute("content", "default");
        themeColorMeta.setAttribute("content", "#ffffff");
      }
    }
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorageService.setTheme(newTheme);
  };

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}